import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const fmt = (v, dp) =>
  v === "" || v === null || v === undefined || Number.isNaN(Number(v))
    ? ""
    : Number(v).toFixed(dp);

/**
 * A price field that always reads as money: $607.70, never $607.7.
 *
 * Formatting happens on blur, not on every keystroke, so typing "607.7" and
 * then "5" isn't fought by the field. While it has focus the text is yours;
 * the moment it loses focus it settles to `decimals` places.
 */
export default function MoneyField({
  label, hint, value, onChange, decimals = 2, step = "0.01",
  disabled, testid, id, prefix = "$", className = "",
}) {
  const [text, setText] = useState(() => fmt(value, decimals));
  const focused = useRef(false);

  // Follow the prop when it changes underneath us (a save round-trip, a
  // reload) — but never while the operator is mid-type.
  useEffect(() => {
    if (!focused.current) setText(fmt(value, decimals));
  }, [value, decimals]);

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id} className="uppercase-label text-[10px]">{label}</Label>
      )}
      <div className="relative">
        {prefix && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 dark:text-slate-400 pointer-events-none">
            {prefix}
          </span>
        )}
        <Input
          id={id}
          type="number"
          step={step}
          min="0"
          inputMode="decimal"
          value={text}
          disabled={disabled}
          onFocus={() => { focused.current = true; }}
          onChange={(e) => {
            setText(e.target.value);
            onChange(e.target.value === "" ? null : Number(e.target.value));
          }}
          onBlur={() => {
            focused.current = false;
            const settled = fmt(text, decimals);
            setText(settled);
            if (settled !== "" && Number(settled) !== Number(value)) onChange(Number(settled));
          }}
          className={`${prefix ? "pl-5" : ""} font-mono tabular-nums`}
          data-testid={testid}
        />
      </div>
      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}
